// ==========================================================
// Promoción de Halloween — flyer del proyecto de Claude Design
// («Flyer Halloween LicitaHUB v2»), traído al portal como modal de bienvenida.
//
// Sale una sola vez por cuenta y dispositivo: se marca como vista en el mismo
// momento en que se muestra —no al cerrarla—, así ni el cierre ni un refresh
// la vuelven a abrir. Pasada la fecha límite no aparece nunca más, de modo que
// el componente puede quedarse montado sin riesgo de mostrar una oferta
// vencida; cuando se quiera retirar basta con sacarlo de `App.js`.
//
// Las tarifas salen de `Plans30` para que promoción y modal de planes no se
// desincronicen: aquí solo se aplica el descuento.
// ==========================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'reactstrap';
import { X, Hourglass, Star, Check, ArrowRight } from 'lucide-react';
import { CICLOS, PLANES } from './Plans30';
import './promo-halloween.scss';

// Último día de la promoción, en hora de Lima (UTC-5).
export const PROMO_HW_FIN_ISO = '2026-10-31';
const FIN_MS = new Date(`${PROMO_HW_FIN_ISO}T23:59:59-05:00`).getTime();

const DESCUENTO = 0.5;            // 50 % OFF
const WHATSAPP = '51942128139';
const FUENTE = 'https://fonts.googleapis.com/css2?family=Creepster&display=swap';

// Una clave por correo: en un equipo compartido cada cuenta la ve una vez.
const claveVista = (email) => `nuvia:promo:halloween-2026:${String(email || 'anon').toLowerCase()}`;

// ¿Sigue viva la promoción? Lo usa el botón de la barra superior, que
// desaparece solo cuando la oferta vence.
export const promoHalloweenVigente = () => Date.now() <= FIN_MS;

// ¿Toca mostrarla sola? Si `localStorage` no está disponible se responde que no:
// sin poder marcarla, saldría en cada carga.
export const promoHalloweenPendiente = (user) => {
  if (!user?.email || Date.now() > FIN_MS) return false;
  try {
    return !localStorage.getItem(claveVista(user.email));
  } catch {
    return false;
  }
};

// Etiquetas del flyer: el modal de planes dice «3 meses», el flyer «Trimestral».
const CICLO_FLYER = {
  mensual: { label: 'Mensual', unidad: 'por 1 mes' },
  trimestral: { label: 'Trimestral', unidad: 'por 3 meses' },
  semestral: { label: 'Semestral', unidad: 'por 6 meses' },
  anual: { label: 'Anual', unidad: 'por 12 meses' }
};

// Los dos planes del flyer, con los textos de la pieza.
const TARJETAS = [
  {
    id: 'basico',
    para: 'Contratos menores',
    beneficios: [
      'Seguimiento de contratos menores',
      'Alertas diarias por correo',
      'Calendario y recordatorios'
    ]
  },
  {
    id: 'plus',
    para: 'Montos mayores',
    insignia: 'EL MÁS ELEGIDO',
    beneficios: [
      'Todo el Básico + Convocatorias SEACE',
      'Calendario de cierres y envíos',
      'Soporte técnico prioritario'
    ]
  }
];

// 67.5 → '67.50'; 135 → '135'
const monto = (n) => (n % 1 ? n.toFixed(2) : String(n));

const tarifa = (plan, ciclo) => {
  const lista = plan.precios[ciclo.id];
  const ahora = lista * DESCUENTO;
  return {
    lista: monto(lista),
    ahora: monto(ahora),
    ahorro: monto(lista - ahora),
    porMes: (ahora / ciclo.meses).toFixed(2)
  };
};

const dosCifras = (n) => String(Math.floor(n)).padStart(2, '0');

const restante = () => {
  const ms = Math.max(0, FIN_MS - Date.now());
  return [
    ['DÍAS', dosCifras(ms / 86400000)],
    ['HORAS', dosCifras((ms / 3600000) % 24)],
    ['MIN', dosCifras((ms / 60000) % 60)],
    ['SEG', dosCifras((ms / 1000) % 60)]
  ];
};

// Calabaza dibujada aquí mismo: el hueco de imagen del flyer quedó vacío y un
// SVG pesa nada, escala sin perder filo y titila con la vela.
const Calabaza = () => (
  <svg className="phw-jack" viewBox="0 0 200 200" aria-hidden="true" focusable="false">
    <defs>
      <radialGradient id="phwJack" cx="36%" cy="28%" r="80%">
        <stop offset="0" stopColor="#FBAE4A" />
        <stop offset="0.55" stopColor="#F28C28" />
        <stop offset="1" stopColor="#C2600F" />
      </radialGradient>
    </defs>
    <path d="M100 60c-4-16 3-28 16-32-4 11-3 18 4 24-8 0-15 3-18 9z" fill="#3E6B45" />
    <ellipse cx="100" cy="120" rx="76" ry="62" fill="url(#phwJack)" />
    <ellipse cx="56" cy="120" rx="25" ry="57" fill="#C2600F" opacity="0.45" />
    <ellipse cx="144" cy="120" rx="25" ry="57" fill="#C2600F" opacity="0.45" />
    <ellipse cx="100" cy="120" rx="29" ry="61" fill="#FBAE4A" opacity="0.4" />
    <g className="phw-jack-face" fill="#FFD98A">
      <path d="M58 100 L94 92 L86 118 Z" />
      <path d="M142 100 L106 92 L114 118 Z" />
      <path d="M100 112 l11 18 h-22 z" />
      <path d="M56 138 L76 138 L84 154 L92 138 L108 138 L116 154 L124 138 L144 138 C140 170, 60 170, 56 138 Z" />
    </g>
  </svg>
);

// `aperturaSolicitada` es un contador, no un booleano: el flyer se cierra solo
// y hay que poder reabrirlo cuantas veces se pulse el botón de la barra.
export default function PromoHalloween({ user, onMostrar, aperturaSolicitada = 0 }) {
  const [abierto, setAbierto] = useState(false);
  const [cicloId, setCicloId] = useState('anual');   // el ciclo que más ahorra
  const [planId, setPlanId] = useState('plus');      // el más elegido, preseleccionado
  const [cuenta, setCuenta] = useState(restante);

  // El aviso a `App` va por referencia: así el efecto de arranque no depende
  // de la identidad de la función y corre una sola vez.
  const avisar = useRef(onMostrar);
  avisar.current = onMostrar;

  useEffect(() => {
    if (!promoHalloweenPendiente(user)) return;
    try {
      localStorage.setItem(claveVista(user.email), new Date().toISOString());
    } catch {}
    avisar.current?.();
    setAbierto(true);
  }, [user]);

  useEffect(() => {
    if (aperturaSolicitada > 0) setAbierto(true);
  }, [aperturaSolicitada]);

  // La fuente de titulares solo se descarga si la promoción llega a verse.
  useEffect(() => {
    if (!abierto || document.querySelector('link[data-phw-font]')) return;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FUENTE;
    link.dataset.phwFont = '1';
    document.head.appendChild(link);
  }, [abierto]);

  useEffect(() => {
    if (!abierto) return undefined;
    setCuenta(restante());
    const id = setInterval(() => setCuenta(restante()), 1000);
    return () => clearInterval(id);
  }, [abierto]);

  // Brasas: posición, tamaño y ritmo fijos por brasa, como en el flyer.
  const brasas = useMemo(
    () =>
      Array.from({ length: 22 }, (_, i) => ({
        left: (i * 47 + 13) % 100,
        size: 3 + ((i * 7) % 6),
        dur: 7 + ((i * 3) % 8),
        delay: -((i * 1.7) % 12)
      })),
    []
  );

  const ciclo = useMemo(() => CICLOS.find((c) => c.id === cicloId) || CICLOS[0], [cicloId]);
  const cerrar = () => setAbierto(false);

  // `window.open` tiene que salir dentro del clic: nada de `await` antes.
  const pedirPorWhatsApp = () => {
    const plan = PLANES.find((p) => p.id === planId);
    const precio = tarifa(plan, ciclo);
    const mensaje = `¡Hola! Quiero aprovechar la promoción de Halloween de LicitaHUB (50 % de descuento).

• Plan: ${plan.nombre}
• Pago: ${ciclo.pedido} — S/${precio.ahora} en lugar de S/${precio.lista}
• Mi correo: ${user?.email || ''}

¿Me indican las formas de pago para activarlo hoy mismo?`;
    window.open(
      `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(mensaje)}`,
      '_blank',
      'noopener,noreferrer'
    );
    cerrar();
  };

  if (!abierto) return null;

  return (
    <Modal
      zIndex={12050}
      isOpen={abierto}
      toggle={cerrar}
      centered
      className="phw"
      labelledBy="phw-title"
    >
      {/* ── Arte: resplandor, niebla, araña y brasas ──────── */}
      <div className="phw-fx" aria-hidden="true">
        <span className="phw-luna" />
        <span className="phw-niebla" />
        <span className="phw-arana"><i /></span>
        {brasas.map((b, i) => (
          <span
            key={i}
            className={`phw-ember ${i % 3 ? '' : 'is-pale'}`}
            style={{
              left: `${b.left}%`,
              width: b.size,
              height: b.size,
              animationDuration: `${b.dur}s`,
              animationDelay: `${b.delay}s`
            }}
          />
        ))}
      </div>

      <div className="phw-body">
        {/* ── Cabecera ───────────────────────────────────── */}
        <div className="phw-top">
          <div className="phw-brand">
            <img src="/nuvia-200x200.jpg" alt="" width="46" height="46" />
            <div>
              <strong>Licita<span>HUB</span></strong>
              <small>Licitaciones del Estado, sin sustos</small>
            </div>
          </div>
          <span className="phw-hash"><i />#HalloweenLicitaHUB</span>
          <button type="button" className="phw-close" onClick={cerrar} aria-label="Cerrar promoción">
            <X size={18} />
          </button>
        </div>

        {/* ── Titular ────────────────────────────────────── */}
        <div className="phw-hero">
          <div className="phw-hero-txt">
            <p className="phw-grito" id="phw-title">¡Precios de miedo!</p>
            <div className="phw-off">
              <span className="phw-50">50</span>
              <span className="phw-off-col">
                <span className="phw-pct">%</span>
                <span className="phw-badge">OFF</span>
              </span>
            </div>
            <p className="phw-sub">
              en <b>todos</b> los planes.<br />Licita más, paga la mitad.
            </p>
          </div>
          <div className="phw-pumpkin">
            <span className="phw-pumpkin-glow" aria-hidden="true" />
            <Calabaza />
          </div>
        </div>

        {/* ── Cuenta atrás ───────────────────────────────── */}
        <div className="phw-urgencia">
          <div className="phw-urgencia-txt">
            <span className="phw-urgencia-label">
              <Hourglass size={14} />
              ÚLTIMAS HORAS
            </span>
            <strong>
              La oferta desaparece el <em>31 de octubre</em>
            </strong>
          </div>
          <div className="phw-reloj" aria-label={`Quedan ${cuenta[0][1]} días`}>
            {cuenta.map(([etiqueta, valor]) => (
              <div key={etiqueta} className="phw-reloj-box">
                <b>{valor}</b>
                <span>{etiqueta}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Selector de ciclo ──────────────────────────── */}
        <div className="phw-elige">
          <span className="phw-elige-label">Elige tu plan</span>
          <div className="phw-ciclo" role="group" aria-label="Ciclo de pago">
            {CICLOS.map((c) => (
              <button
                key={c.id}
                type="button"
                aria-pressed={c.id === cicloId}
                className={c.id === cicloId ? 'is-on' : ''}
                onClick={() => setCicloId(c.id)}
              >
                {CICLO_FLYER[c.id].label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Planes ─────────────────────────────────────── */}
        <div className="phw-planes" role="radiogroup" aria-label="Plan que quieres activar">
          {TARJETAS.map((t) => {
            const plan = PLANES.find((p) => p.id === t.id);
            const precio = tarifa(plan, ciclo);
            const elegido = t.id === planId;

            // Tarjeta entera seleccionable: el anillo naranja marca el plan
            // que viaja en el mensaje de WhatsApp. Va como `radio` y no como
            // botón porque dentro hay titular y lista.
            return (
              <div
                key={t.id}
                role="radio"
                tabIndex={0}
                aria-checked={elegido}
                onClick={() => setPlanId(t.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPlanId(t.id);
                  }
                }}
                className={`phw-card n-${t.id} ${elegido ? 'is-on' : ''}`}
              >
                <div className="phw-card-head">
                  <h3>{plan.nombre}</h3>
                  {t.insignia && (
                    <span className="phw-flag">
                      <Star size={12} />
                      {t.insignia}
                    </span>
                  )}
                  <small>{t.para}</small>
                </div>

                <div className="phw-precio" key={`${cicloId}-${t.id}`}>
                  <b><i>S/</i>{precio.ahora}</b>
                  <span>
                    <s>S/{precio.lista}</s>
                    <em>{CICLO_FLYER[cicloId].unidad}</em>
                  </span>
                </div>

                <div className="phw-chips">
                  <span className="is-save">Ahorras S/{precio.ahorro}</span>
                  <span>≈ S/{precio.porMes} al mes</span>
                </div>

                <ul className="phw-benef">
                  {t.beneficios.map((b) => (
                    <li key={b}><Check size={15} />{b}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* ── Cierre ─────────────────────────────────────── */}
        <div className="phw-cierre">
          <button type="button" className="phw-cta" onClick={pedirPorWhatsApp}>
            Activa tu plan por WhatsApp
            <ArrowRight size={24} />
          </button>
          <div className="phw-cierre-txt">
            <strong>Activación el mismo día</strong>
            <span>Válido hasta el 31/10, 11:59 p. m.</span>
          </div>
        </div>
      </div>
    </Modal>
  );
}
